/**
 * ConcoursStats - Statistics panel for PKA Ton Concours
 */
import {
  AlertCircle,
  BarChart3,
  Bookmark,
  CheckCircle,
  Eye,
  FileText,
  RefreshCw,
  TrendingUp
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { concoursApiClient } from '../../services/concoursApi';
import {
  CONCOURS_STATUS_COLORS,
  CONCOURS_STATUS_LABELS,
  ConcoursStatus
} from '../../types/concours';

const ConcoursStats: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await concoursApiClient.getStats();
      setStats(response.data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex justify-center items-center">
        <RefreshCw className="w-6 h-6 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-500" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchStats}
          className="ml-auto text-red-600 hover:text-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-4">
      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Concours</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">En Cours</p>
              <p className="text-2xl font-bold text-green-600">
                {(stats.by_status?.open || 0) + (stats.by_status?.in_progress || 0)}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Mis en Avant</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.featured}</p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Populaires</p>
              <p className="text-2xl font-bold text-purple-600">{stats.popular}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Bookmark className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gray-500" />
            Par Statut
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.by_status || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CONCOURS_STATUS_COLORS[status as ConcoursStatus]}`}>
                  {CONCOURS_STATUS_LABELS[status as ConcoursStatus]}
                </span>
                <span className="text-sm font-medium text-gray-700">{count as number}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gray-500" />
            Par Catégorie
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.by_category || {}).map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{category}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${((count as number) / stats.total) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-8 text-right">{count as number}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Most Viewed */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-500" />
            Plus Consultés
          </h3>
          <div className="space-y-3">
            {(stats.most_viewed || []).slice(0, 5).map((item: any, index: number) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <span className="text-sm text-gray-700 truncate max-w-[180px]">{item.title}</span>
                </div>
                <span className="text-sm text-gray-500">{item.views} vues</span>
              </div>
            ))}
            {(!stats.most_viewed || stats.most_viewed.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">Aucune donnée disponible</p>
            )}
          </div>
        </div>

        {/* Most Saved */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-gray-500" />
            Plus Sauvegardés
          </h3>
          <div className="space-y-3">
            {(stats.most_saved || []).slice(0, 5).map((item: any, index: number) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <span className="text-sm text-gray-700 truncate max-w-[180px]">{item.title}</span>
                </div>
                <span className="text-sm text-gray-500">{item.saves} sauv.</span>
              </div>
            ))}
            {(!stats.most_saved || stats.most_saved.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">Aucune donnée disponible</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConcoursStats;
